import { useAppState } from "@/store/useApp";
import { useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/use-api";
import { useHotkeys } from "react-hotkeys-hook";
import { Navbar } from "./Navbar";
import Loading from "./Loading";

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout = ({ children }: LayoutProps) => {
  const focusMode = useAppState((state) => state.focusMode);
  const currentDocument = useAppState((state) => state.currentDocument);
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { fetchDocumentData: fetchDocumentData } = useApi();
  const assessments = useAppState((state) => state.assessments);

  // Load document if not already loaded or if document ID doesn't match
  useEffect(() => {
    const loadDocument = async () => {
      if (
        documentId &&
        (!currentDocument || currentDocument.id !== documentId)
      ) {
        await fetchDocumentData(documentId);
      }
    };
    if (assessments !== null) {
      loadDocument();
    }
  }, [documentId, currentDocument, fetchDocumentData, assessments]);

  // Reader mode navigation shortcuts
  useHotkeys(
    "mod+1",
    () => {
      if (documentId) {
        navigate(`/documents/${documentId}`);
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    },
    [documentId, navigate]
  );

  useHotkeys(
    "mod+2",
    () => {
      if (documentId) {
        navigate(`/documents/${documentId}/rubric`);
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    },
    [documentId, navigate]
  );

  useHotkeys(
    "mod+3",
    () => {
      if (documentId) {
        navigate(`/documents/${documentId}/pdf`);
      }
    },
    {
      preventDefault: true,
      enableOnFormTags: false,
    },
    [documentId, navigate]
  );

  // Show loading state while document is being fetched
  if (!currentDocument || currentDocument.id !== documentId) {
    return <Loading />;
  }

  return (
    <div className="h-screen overflow-hidden bg-[#FFFEFD]">
      {!focusMode && <Navbar />}
      <div className={`flex h-[calc(100vh-0rem)] ${!focusMode ? "pt-16" : ""}`}>
        {children}
      </div>
    </div>
  );
};
